import { Box, Link, Typography } from "@mui/material";
import type * as React from "react";
import { COLOGUARD_WEBSITE_URL } from "./constants";
import { FaqBody, MarketingAccordionItem } from "./MarketingAccordion";

const publicPolicyLinkSx = {
  color: "inherit",
  textDecoration: "underline",
  textUnderlineOffset: "2px"
} as const;

const FaqAccordions: React.FC = () => (
  <Box
    component="section"
    sx={{
      display: "flex",
      flexDirection: "column",
      gap: 2,
      width: "100%"
    }}
  >
    <Typography component="h2" variant="body_strong_xl_black" sx={{ m: 0 }}>
      Frequently asked questions
    </Typography>

    <Box sx={{ display: "flex", flexDirection: "column" }}>
      <MarketingAccordionItem title="Why is Genomic Life connecting me with Cologuard?">
        <FaqBody>
          About 1 in 3 Americans are behind on their colon cancer screening.
          That&apos;s concerning because colon cancer is becoming more common in
          younger people.
        </FaqBody>
        <FaqBody>
          Genomic Life is dedicated to helping members stay up-to-date with
          routine cancer screenings. That can help identify cancer early, when
          it&apos;s generally easier to treat.
        </FaqBody>
        <FaqBody>
          By helping members check if they&apos;re due for colon cancer
          screening and offering a proven option that can be started from home,
          our goal is to help more members get screened.
        </FaqBody>
        <Typography
          component="p"
          sx={{ m: 0, typography: "body_black", fontStyle: "italic" }}
        >
          Please note that Cologuard testing is not included with the Genomic
          Life membership; instead, it will be billed to your health insurance.
          One-on-one navigation support is included in your Genomic Life
          Membership. Navigators are available both before testing to determine
          if Cologuard is right for you and after testing to understand the
          results.
        </Typography>
      </MarketingAccordionItem>

      <MarketingAccordionItem title="Is a colonoscopy better than Cologuard?">
        <FaqBody>
          The best colon cancer screening option for you is the one that you
          will complete.
        </FaqBody>
        <FaqBody>
          Colonoscopies both look for cancer and remove abnormal growths.
          However, many people put off their colonoscopy because it can seem
          scary and often requires time away from work.
        </FaqBody>
        <FaqBody>
          Cologuard can detect evidence of cancer or polyps (growths that have
          the potential to turn into cancer if not removed).
        </FaqBody>
      </MarketingAccordionItem>

      <MarketingAccordionItem title="What does a positive Cologuard result mean?">
        <FaqBody>
          If your test comes back positive, that means the test found a signal
          of colon cancer.† This does not prove you have colon cancer. Next
          steps include a conversation with your healthcare provider; a
          colonoscopy is also often recommended.
        </FaqBody>
      </MarketingAccordionItem>

      <MarketingAccordionItem title="What does a negative Cologuard result mean?">
        <FaqBody>
          A negative result means the test did not find a signal of colon
          cancer.† This does not completely rule out the possibility of colon
          cancer. Be sure to stay up to date on all recommended cancer
          screenings. If you’re not sure how to do that, Genomic Life’s
          navigation team can help.
        </FaqBody>
      </MarketingAccordionItem>

      <MarketingAccordionItem title="What does Cologuard cost?">
        <FaqBody>
          Most insured patients who are 45+ and at average risk pay $0 out of
          pocket.‡ However, there are some instances where Cologuard is only
          partially covered, or not at all. See the{" "}
          <Link
            href={COLOGUARD_WEBSITE_URL}
            rel="noopener noreferrer"
            sx={publicPolicyLinkSx}
            target="_blank"
          >
            Cologuard website
          </Link>{" "}
          or contact your benefits administrator for more information.
        </FaqBody>
        <FaqBody>
          If you qualify for Cologuard and choose to get testing, the test will
          be billed to your health insurance by Exact Sciences. Nationwide, over
          96% of all Cologuard patients 45 and over have no anticipated
          out-of-pocket costs.‡
        </FaqBody>
      </MarketingAccordionItem>
    </Box>
  </Box>
);

export default FaqAccordions;
